import React from 'react';
import twemoji from 'twemoji';

/**
 * EmojiParser
 *
 * Custom implementation of the WordPress emoji parser "wp-emoji-release.min.js"
 * https://github.com/WordPress/wordpress-develop/blob/6f6bd30d37360670cc1daa96d08dcbac2f038c07/src/js/_enqueues/wp/emoji.js
 *
 * This is due to a bug where we cannot turn off the emoji parser in certain scenarios. (lexical editor)
 */
class EmojiParser {
  private active = true;
  private loaded = false;

  public static _DEFAULTS = {
    baseUrl: 'https://s.w.org/images/core/emoji/15.0.3/72x72/',
    ext: '.png',
    svgUrl: 'https://s.w.org/images/core/emoji/15.0.3/svg/',
    svgExt: '.svg',
  } as const;

  constructor() {
    this.init();
  }

  private init() {
    if (this.loaded) {
      return;
    }

    this.loaded = true;

    new MutationObserver((mutationRecords) => {
      if (!this.active) {
        return;
      }

      let i = mutationRecords.length;
      let addedNodes: NodeList;
      let removedNodes: NodeList;
      let ii = 0;
      let node: Node;

      while (i--) {
        addedNodes = mutationRecords[i].addedNodes;
        removedNodes = mutationRecords[i].removedNodes;
        ii = addedNodes.length;

        /*
         * Checks if an image has been replaced by a text element
         * with the same text as the alternate description of the replaced image.
         * (presumably because the image could not be loaded).
         * If it is, do absolutely nothing.
         *
         * Node type 3 is a TEXT_NODE.
         *
         * @link https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
         */
        if (
          ii === 1 &&
          removedNodes.length === 1 &&
          addedNodes[0].nodeType === 3 &&
          removedNodes[0].nodeName === 'IMG' &&
          // @ts-ignore
          addedNodes[0]?.data === removedNodes[0]?.alt &&
          // @ts-ignore
          'load-failed' === removedNodes[0].getAttribute('data-error')
        ) {
          return;
        }

        // Loop through all the added nodes.
        while (ii--) {
          node = addedNodes[ii];

          // Node type 3 is a TEXT_NODE.
          if (node.nodeType === 3) {
            if (!node.parentNode) {
              continue;
            }

            node = node.parentNode;
          }

          if (this.test(node?.textContent)) {
            this.parse(node as HTMLElement);
          }
        }
      }
    }).observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.parse(document.body);
  }

  // Single char. U+20E3 to detect keycaps. U+00A9 "copyright sign" and U+00AE "registered sign" not included.
  private static REGEX_SINGLE =
    // biome-ignore lint/suspicious/noMisleadingCharacterClass: <explanation>
    /[\u203C\u2049\u20E3\u2122\u2139\u2194-\u2199\u21A9\u21AA\u2300\u231A\u231B\u2328\u2388\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u2618\u261D\u2620\u2622\u2623\u2626\u262A\u262E\u262F\u2638\u2639\u263A\u2648-\u2653\u2660\u2663\u2665\u2666\u2668\u267B\u267F\u2692\u2693\u2694\u2696\u2697\u2699\u269B\u269C\u26A0\u26A1\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26C8\u26CE\u26CF\u26D1\u26D3\u26D4\u26E9\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753\u2754\u2755\u2757\u2763\u2764\u2795\u2796\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05\u2B06\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]/;

  // Surrogate pair range. Only tests for the second half.
  private static REGEX_PAIR = /[\uDC00-\uDFFF]/;

  public test(value?: string | null): boolean {
    if (!this.active) {
      return false;
    }
    if (!value || typeof value !== 'string' || value?.length < 2) {
      return false;
    }

    return EmojiParser.REGEX_PAIR.test(value) || EmojiParser.REGEX_SINGLE.test(value);
  }

  public parse(node: string | HTMLElement, config: Partial<TwemojiOptions | ParseCallback> = {}) {
    return twemoji.parse(node, {
      base: EmojiParser._DEFAULTS.svgUrl,
      ext: EmojiParser._DEFAULTS.svgExt,
      className: 'emoji',
      attributes: () => {
        return {
          role: 'img',
          crossorigin: 'anonymous',
        };
      },
      callback: (icon, options, variant) => {
        // Ignore some standard characters that TinyMCE recommends in its character map.
        switch (icon) {
          case 'a9':
          case 'ae':
          case '2122':
          case '2194':
          case '2660':
          case '2663':
          case '2665':
          case '2666':
            return false;
        }

        const opts = options as {
          base: string;
          ext: string;
          size: string;
          className: string;
        };

        if (opts?.base && icon && opts?.ext) {
          return ''.concat(opts.base, icon, opts.ext);
        }

        return false;
      },
      ...config,
    });
  }

  public setActive(active: boolean) {
    this.active = active;
  }
}

window.zn = window?.zn || {};

window.addEventListener('DOMContentLoaded', () => {
  if (!!window?.zn?.emojiParser && window?.zn?.emojiParser instanceof EmojiParser) {
    return;
  }

  window.zn.emojiParser = new EmojiParser();
});

export type { EmojiParser };

/**
 * Disables the emoji parser for the current component.
 */
export const useNoEmojiParser = () => {
  React.useEffect(() => {
    window?.zn?.emojiParser?.setActive?.(false);

    return () => {
      window?.zn?.emojiParser?.setActive?.(true);
    };
  }, []);
};
